import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AppBar,
  Box,
  Breadcrumbs,
  CircularProgress,
  Grid,
  Link,
  Stack,
  Snackbar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

import Navbar from "../../components/common/Navbar";
import SearchAppBar from "../../components/common/search";
import ViewData from "../../components/grids/ViewDatagrid";
import { useStyles } from "../../assets/styles/Theme";
import { externalgetApi } from "../../services/external/externalApis";
import { STOCK_API_03 } from "../../api/ApiModel";
import { stockbalancedata } from "../../assets/Griddata";
import { CON_STATUS_DELETED } from "../../utils/sys/Constants";

const safeStr = (val) => {
  if (val == null || typeof val === "object") return "";
  return String(val);
};

const safeNum = (val) => {
  const n = parseFloat(String(val ?? "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "";
  return new Intl.NumberFormat("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const isValidAsAtDate = (dateValue) =>
  dateValue != null && dayjs(dateValue).isValid();

function StockBalance() {
  const theme = useTheme();
  const styles = useStyles(theme);

  const SYS_USER = useSelector((state) => state.user) || {};
  const { COMPCODE, LOCCODE, RESTCODE } = SYS_USER.USER_PARAM || {};

  const [asAtDate, setAsAtDate] = useState(dayjs());
  const [rawRows, setRawRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateError, setDateError] = useState(null);
  const [searchText, setSearchText] = useState("");

  const fetchBalance = async (dateValue) => {
    if (!isValidAsAtDate(dateValue)) {
      setDateError("Please select a valid as at date.");
      return;
    }

    setDateError(null);
    setIsLoading(true);
    setError(null);
    try {
      const result = await externalgetApi(STOCK_API_03, "GET", {
        COMPCODE,
        LOCCODE,
        RESTCODE,
        ASATDATE: dayjs(dateValue).format("YYYY-MM-DD"),
        STSD: CON_STATUS_DELETED,
      });
      setRawRows(Array.isArray(result) ? result : []);
    } catch (err) {
      setError(err?.message || "Failed to load stock balance.");
      setRawRows([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAsAtDateChange = (newValue) => {
    if (!newValue || !dayjs(newValue).isValid()) {
      setDateError("Please select a valid as at date.");
      return;
    }
    setDateError(null);
    setAsAtDate(newValue);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  useEffect(() => {
    if (!isValidAsAtDate(asAtDate)) return;
    fetchBalance(asAtDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asAtDate, COMPCODE, LOCCODE, RESTCODE]);

  const mappedRows = useMemo(() => {
    const rows = rawRows.map((r, idx) => ({
      REFNO: idx + 1,
      ITEMCODE: safeStr(r.MENUCODE ?? r.ITEMCODE),
      ITEMCATEGORY: safeStr(r.MENUCATNAME ?? r.ITEMCATEGORY),
      ITEMNAME: safeStr(r.MENUNAME ?? r.ITEMNAME),
      STOCKIN: safeNum(r.STOCKIN),
      STOCKOUT: safeNum(r.STOCKOUT),
      RETURNQTY: safeNum(r.RETURNS ?? r.RETURNQTY),
      STOCKBALANCE: safeNum(r.STOCKBALANCE),
    }));

    if (!searchText) return rows;
    const lower = searchText.toLowerCase();
    return rows.filter(
      (row) =>
        (row.ITEMCODE || "").toLowerCase().includes(lower) ||
        (row.ITEMCATEGORY || "").toLowerCase().includes(lower) ||
        (row.ITEMNAME || "").toLowerCase().includes(lower)
    );
  }, [rawRows, searchText]);

  return (
    <div>
      <Navbar />
      <AppBar sx={styles.AI_CustomAppBar1}>
        <Box sx={styles.AI_HeaderContainer1}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Breadcrumbs
                sx={styles.AI_breadcrumbs2}
                separator={<NavigateNextIcon fontSize="small" />}
              >
                <Link underline="none">Home</Link>
                <Link underline="none">Stock</Link>
                <Link underline="none">Stock Balance</Link>
              </Breadcrumbs>
            </Grid>
            <Grid item xs={12} md={6} p={2}>
              <Stack
                sx={styles.AI_fixedStack1}
                direction="row"
                justifyContent="flex-end"
                alignItems="center"
                spacing={2}
                padding={1}
                ml={1}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="As at date"
                    value={asAtDate}
                    onChange={handleAsAtDateChange}
                    format="YYYY-MM-DD"
                    disabled={isLoading}
                    slotProps={{
                      textField: {
                        size: "small",
                        error: Boolean(dateError),
                        sx: {
                          width: 170,
                          "& .MuiInputBase-root": { height: "40px" },
                          "& .MuiInputAdornment-root .MuiIconButton-root": {
                            color: theme.palette.primary.main,
                          },
                        },
                      },
                      popper: {
                        sx: {
                          "& .MuiPaper-root": {
                            backgroundColor: theme.palette.background.paper,
                            boxShadow: "none",
                          },
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
                <SearchAppBar onChange={handleSearchChange} value={searchText} />
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </AppBar>

      <Snackbar
        open={Boolean(dateError)}
        autoHideDuration={4000}
        onClose={() => setDateError(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="warning" onClose={() => setDateError(null)}>
          {dateError}
        </Alert>
      </Snackbar>

      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "60vh",
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      ) : error ? (
        <Box sx={{ px: 2, mt: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      ) : (
        <ViewData
          columns={stockbalancedata.columns}
          rows={mappedRows.map((r) => ({ ...r, id: r.ITEMCODE || r.REFNO }))}
          menuItems={[]}
          tabletitle={stockbalancedata.name}
          gridid="ITEMCODE"
          displayActions={false}
        />
      )}
    </div>
  );
}

export default StockBalance;
